import React, { useState, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { NeumorphicContainer } from "./NeumorphicContainer";
import imageSrc from "../../assets/images/phone-user.png";

// Simplified State Type: Just a string for the transform property
type TransformStyle = {
  transform: string;
};

// Common CSS to ensure smooth movement (applied with the dynamic transform)
const COMMON_STYLE = {
  transition: 'transform 0.1s linear',
  willChange: 'transform',
};
const MOBILE_ENTRANCE_STYLE = {
  transition: 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)', // Spring-like animation
  willChange: 'transform',
};


// Initial/Neutral State
const NEUTRAL_TRANSFORM = 'translateY(0px) rotateZ(0deg)';
const NEUTRAL_INNER_TRANSFORM = 'translateY(0px) translateX(0px)';

// --- Breakpoint Constant ---
const ENABLE_PARALLAX_BREAKPOINT = 768;


const Banner: React.FC = () => {
  const { t } = useTranslation();

  const bannerRef = useRef<HTMLElement>(null);
  const textColumnRef = useRef<HTMLDivElement>(null);
  const imageColumnRef = useRef<HTMLDivElement>(null);
  const imageElementRef = useRef<HTMLImageElement>(null);

  const [textStyle, setTextStyle] = useState<TransformStyle>({ transform: NEUTRAL_TRANSFORM });
  const [imageStyle, setImageStyle] = useState<TransformStyle>({ transform: NEUTRAL_TRANSFORM });
  const [innerTextStyle, setInnerTextStyle] = useState<TransformStyle>({ transform: NEUTRAL_INNER_TRANSFORM });
  const [innerImageStyle, setInnerImageStyle] = useState<TransformStyle>({ transform: NEUTRAL_INNER_TRANSFORM });


  useLayoutEffect(() => {

    const mediaQuery = window.matchMedia(`(min-width: ${ENABLE_PARALLAX_BREAKPOINT}px)`);
    let isDesktop = mediaQuery.matches;

    // --- Media Query Listener to Enable/Disable ---
    const handleMediaQueryChange = (event: MediaQueryListEvent) => {
      isDesktop = event.matches; // Update desktop status
      handleScroll(); // Recalculate on breakpoint change
    };

    mediaQuery.addEventListener('change', handleMediaQueryChange);

    // --- Scroll Handler Logic ---
    const handleScroll = () => {

      const currentBanner = bannerRef.current;
      const imageElement = imageElementRef.current;

      if (!currentBanner || !imageElement) return;

      const element = currentBanner;
      const rect = element.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const imageRect = imageElement.getBoundingClientRect();


      // --- Logic for all breakpoints to reset when scrollY is 0 ---
      if (window.scrollY === 0) {
        setTextStyle({ transform: NEUTRAL_TRANSFORM });
        setImageStyle({ transform: NEUTRAL_TRANSFORM });
        setInnerTextStyle({ transform: NEUTRAL_INNER_TRANSFORM });
        setInnerImageStyle({ transform: NEUTRAL_INNER_TRANSFORM });
        return; // Reset and exit when at the very top
      }

      // --- MOBILE LOGIC (Entrance Animation) ---
      if (!isDesktop) {
        const mobileAnimationDistance = 100; // The amount the image slides up (in px)

        const scrollProgress = 1 - (imageRect.top / viewportHeight);
        const progress = Math.min(1, Math.max(0, scrollProgress));
        const translateY = mobileAnimationDistance * (1 - progress);

        setInnerImageStyle({ ...MOBILE_ENTRANCE_STYLE, transform: `translateY(${translateY}px)` });

        // Reset other styles to neutral on mobile
        setTextStyle({ transform: NEUTRAL_TRANSFORM });
        setImageStyle({ transform: NEUTRAL_TRANSFORM });
        setInnerTextStyle({ transform: NEUTRAL_INNER_TRANSFORM });

        return; // Exit here on mobile
      }


      // --- DESKTOP/TABLET LOGIC (Parallax) ---

      // Calculate Scroll Value and Clamped Factor (rest of the logic)
      const centerDistance = (rect.top + rect.height / 2) - viewportHeight / 2;
      const normalizedScroll = centerDistance / (viewportHeight / 1.5);
      const clampedScroll = normalizedScroll * 0.6;

      // --- Visibility Factor (Dampening for exiting the viewport) ---
      const elementHeight = rect.height;
      const visibleStart = Math.max(0, viewportHeight - rect.top);
      const visibleEnd = Math.max(0, rect.bottom);
      const visibleHeight = Math.min(elementHeight, visibleStart, visibleEnd);
      const visibilityFactor = Math.min(1, Math.max(0, visibleHeight / (elementHeight * 0.5)));


      // --- Reset logic for when element is completely off-screen ---
      if (visibilityFactor === 0) {
        setTextStyle({ transform: NEUTRAL_TRANSFORM });
        setImageStyle({ transform: NEUTRAL_TRANSFORM });
        setInnerTextStyle({ transform: NEUTRAL_INNER_TRANSFORM });
        setInnerImageStyle({ transform: NEUTRAL_INNER_TRANSFORM });
        return;
      }

      // --- Calculate Transforms based on factor ---
      const animationFactor = clampedScroll * visibilityFactor;

      // 1. Column Animation Styles
      const textTranslateY = animationFactor * -50;
      const textRotateZ = animationFactor * 16;
      setTextStyle({ ...COMMON_STYLE, transform: `translateY(${textTranslateY}px) rotateZ(${textRotateZ}deg)` });

      const imageTranslateY = animationFactor * 30;
      const imageRotateZ = animationFactor * -30;
      setImageStyle({ ...COMMON_STYLE, transform: `translateY(${imageTranslateY}px) rotateZ(${imageRotateZ}deg)` });

      // 2. Inner Element Animation Styles (Desktop/Tablet)
      const innerTextTranslateY = animationFactor * 10;
      const innerTextTranslateX = animationFactor * 5;
      setInnerTextStyle({ ...COMMON_STYLE, transform: `translateY(${innerTextTranslateY}px) translateX(${innerTextTranslateX}px)` });

      const innerImageTranslateY = animationFactor * -7;
      const innerImageTranslateX = animationFactor * -3;
      setInnerImageStyle({ ...COMMON_STYLE, transform: `translateY(${innerImageTranslateY}px) translateX(${innerImageTranslateX}px)` });
    };

    // CRITICAL: Attach the scroll listener regardless of breakpoint.
    window.addEventListener('scroll', handleScroll);
    handleScroll();

    // --- Cleanup ---
    return () => {
      mediaQuery.removeEventListener('change', handleMediaQueryChange);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // --- Render ---

  return (
    <section
      ref={bannerRef}
      className="banner neumorphic-container rounded-0 d-flex justify-content-center align-items-center mb-0 p-5"
    >
      <div className="row justify-content-center">

        {/* Text Column - Parent Style */}
        <div
          className="col-12 col-lg-6"
          ref={textColumnRef}
          style={{ ...textStyle }}
        >
          {/* Apply Inner Text Style to Title and Subtitle container */}
          <div style={{ ...innerTextStyle }}>
            <h1 className="title gradient">{t("banner.title")}</h1>
            <p>{t("banner.subtitle")}</p>
          </div>

          <NeumorphicContainer className="banner-download-container">
            <p className="fw-bold text-center">{t("banner.download")}</p>
            <p className="text-center text-center">{t("banner.comingSoon")}</p>
          </NeumorphicContainer>
        </div>

        {/* Image Column - Parent Style */}
        <div
          className="col-12 col-lg-6 d-flex justify-content-center align-items-center"
          ref={imageColumnRef}
          style={{ ...imageStyle }}
        >
          <div className="neumorphic-container oval oval-lg overflow-hidden position-relative
          d-flex justify-content-center align-items-center my-4">
            <div className="neumorphic-container gradient oval oval-md">
              <img
                ref={imageElementRef} // Attach the Ref here
                src={imageSrc}
                className="col-12 position-absolute bottom-sm-0 top-lg-0 start-0"
                alt="logo-white"
                style={{ ...innerImageStyle }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Banner;
